import jsPDF from "jspdf"; // if error, try: import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { apiUrl } from "../constants";
import {
  exportFontSemiBold,
  exportFontBold,
  exportFontExtraBold,
} from "../helperFunctions/fonts";

function drawMarkerDot(
  ctx,
  x,
  y,
  {
    radius = 5, // size in canvas pixels (remember: canvas is @2x)
    fill = "#1E88E5", // blue
    stroke = "#ffffff", // white outline (optional)
    strokeWidth = 2,
  } = {}
) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  if (stroke && strokeWidth > 0) {
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = stroke;
    ctx.stroke();
  }
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.restore();
}

export function loadImage(src, crossOrigin = "anonymous") {
  return new Promise((resolve, reject) => {
    const img = new Image();

    const isData = typeof src === "string" && src.startsWith("data:");
    if (crossOrigin && !isData) {
      img.crossOrigin = crossOrigin;
    }

    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function lngLatToWorldPx(lon, lat, zoom, tileSize = 512) {
  const λ = (lon + 180) / 360; // [0..1]
  const sin = Math.sin((lat * Math.PI) / 180);
  const x = λ;
  const y = 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
  const scale = tileSize * Math.pow(2, zoom);
  return { x: x * scale, y: y * scale };
}

async function buildStaticMapURLWithPin({
  mapRef,
  mapboxToken,
  targetW,
  targetH,
  markerLngLat, // [lon, lat]
  pinColor = "1E88E5", // hex without '#'
  zoomAdjust = 0,
}) {
  const map = mapRef?.current?.getMap?.();
  if (!mapboxToken || !map) return null;

  const center = map.getCenter?.();
  const zoom = map.getZoom?.();

  const lng = center?.lng ?? 0;
  const lat = center?.lat ?? 0;

  const w = Math.max(1, Math.min(1280, Math.round(targetW || 600)));
  const h = Math.max(1, Math.min(1280, Math.round(targetH || 300)));

  // Same overlay that worked before
  let overlay = "";
  if (
    Array.isArray(markerLngLat) &&
    Number.isFinite(markerLngLat[0]) &&
    Number.isFinite(markerLngLat[1])
  ) {
    overlay = `pin-s+${pinColor}(${markerLngLat[0].toFixed(
      6
    )},${markerLngLat[1].toFixed(6)})/`;
  }
  const z = Math.max(0, Math.min(22, (zoom ?? 12) + zoomAdjust));
  const url =
    `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/` +
    `${overlay}${lng},${lat},${z.toFixed(2)},0,0/` +
    `${w}x${h}@2x?access_token=${encodeURIComponent(mapboxToken)}`;

  return { url, zoom: zoom ?? 12, width: w, height: h };
}

async function loadImageStrict(src) {
  try {
    return await loadImage(src, "anonymous");
  } catch (e) {
    console.error("[loadImageStrict] Failed to load:", src, e);
    throw e;
  }
}

async function renderMapWithTinyMarker({
  mapRef,
  mapboxToken,
  markerLngLat,
  mapWidthPx,
  mapHeightPx,
  zoomAdjust = 0,
}) {
  // --- Try STATIC with Mapbox pin-s overlay (like the old working code) ---
  try {
    const meta = await buildStaticMapURLWithPin({
      mapRef,
      mapboxToken,
      targetW: mapWidthPx,
      targetH: mapHeightPx,
      markerLngLat,
      pinColor: "1E88E5", // same brand blue; tweak if you want
      zoomAdjust,
    });
    if (!meta) throw new Error("static-meta-missing");

    const mapImg = await loadImageStrict(meta.url);

    const canvas = document.createElement("canvas");
    canvas.width = meta.width * 2;
    canvas.height = meta.height * 2;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(mapImg, 0, 0);
    return canvas.toDataURL("image/png");
  } catch (err) {
    console.warn("[Static pin overlay failed] fallback to local draw:", err);
  }

  // --- Fallback (unchanged): snapshot live map & draw a dot so you still get a marker ---
  const liveMap = mapRef?.current?.getMap?.();
  if (!liveMap) throw new Error("Map instance not available for fallback");

  const liveCanvas = liveMap.getCanvas();
  const liveW = liveCanvas.width;
  const liveH = liveCanvas.height;

  const outCanvas = document.createElement("canvas");
  outCanvas.width = mapWidthPx * 2;
  outCanvas.height = mapHeightPx * 2;
  const octx = outCanvas.getContext("2d");

  octx.drawImage(
    liveCanvas,
    0,
    0,
    liveW,
    liveH,
    0,
    0,
    outCanvas.width,
    outCanvas.height
  );

  if (
    Array.isArray(markerLngLat) &&
    Number.isFinite(markerLngLat[0]) &&
    Number.isFinite(markerLngLat[1])
  ) {
    const ptCss = liveMap.project({
      lng: markerLngLat[0],
      lat: markerLngLat[1],
    });
    const dpr = window.devicePixelRatio || 1;
    const ptLivePx = { x: ptCss.x * dpr, y: ptCss.y * dpr };

    const scaleX = outCanvas.width / liveW;
    const scaleY = outCanvas.height / liveH;

    const x2x = ptLivePx.x * scaleX;
    const y2x = ptLivePx.y * scaleY;

    drawMarkerDot(octx, Math.round(x2x), Math.round(y2x), {
      radius: 10,
      fill: "#1E88E5",
      stroke: "#ffffff",
      strokeWidth: 2,
    });
  }

  return outCanvas.toDataURL("image/png");
}

async function captureNodeAtFixedWidth(
  infoEl,
  cssWidthPx,
  { hideElement } = {}
) {
  // offscreen host
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "0";
  host.style.width = cssWidthPx + "px";
  host.style.pointerEvents = "none";
  document.body.appendChild(host);

  // deep clone & force fixed width
  const clone = infoEl.cloneNode(true);
  clone.style.width = cssWidthPx + "px";
  clone.style.maxWidth = cssWidthPx + "px";
  clone.style.boxSizing = "border-box";
  host.appendChild(clone);

  // (optionally) hide an element inside the clone
  if (hideElement instanceof HTMLElement) {
    const cls = hideElement.className
      ? "." + hideElement.className.split(" ").join(".")
      : null;
    const elInClone = cls ? clone.querySelector(cls) : null;
    if (elInClone) elInClone.style.display = "none";
  }

  // render the CLONE (stable width) → canvas
  const canvas = await html2canvas(clone, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    // Force a stable "viewport" for responsive CSS:
    windowWidth: cssWidthPx,
    width: cssWidthPx,
  });

  // cleanup
  document.body.removeChild(host);

  return {
    dataURL: canvas.toDataURL("image/png"),
    w: canvas.width,
    h: canvas.height,
  };
}

export async function downloadDeliveryA4({
  // Map & statuses
  mapRef,
  mapboxToken,
  markerLngLat,
  infoEl,
  hideElement = null,
  coordinates,

  // Order/customer fields
  orderNumber,
  driverName,
  orderDate,
  customerName,
  pickupAddress,
  dropoffAddress,

  // Backend assets
  packageId,
  authToken,

  // Branding / assets
  logoSrc,
  brand = { primary: "#121B3F", muted: "#676C7E" },
  title = "Delivery Notification",
  fontSemiBoldBase64 = exportFontSemiBold,

  // Output
  fileName = "delivery.pdf",
}) {
  mapRef.current?.setCenter([coordinates.longitude, coordinates.latitude]);
  mapRef.current?.setZoom(14);

  const pdf = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth(); // 210
  const pageH = pdf.internal.pageSize.getHeight(); // 297
  const m = { top: 18, right: 15, bottom: 18, left: 15 };

  // ---------- Fonts ----------
  const hasSemi = !!exportFontSemiBold;
  const hasBold = !!exportFontBold;
  const hasXBold = !!exportFontExtraBold;

  if (hasSemi) {
    pdf.addFileToVFS("App-SemiBold.ttf", exportFontSemiBold);
    pdf.addFont("App-SemiBold.ttf", "AppFont", "semibold");
  }
  if (hasBold) {
    pdf.addFileToVFS("App-Bold.ttf", exportFontBold);
    pdf.addFont("App-Bold.ttf", "AppFont", "bold");
  }
  if (hasXBold) {
    pdf.addFileToVFS("App-ExtraBold.ttf", exportFontExtraBold);
    pdf.addFont("App-ExtraBold.ttf", "AppFont", "extrabold");
  }

  // Pick sensible defaults (since you don’t have Regular):
  const useSemi = hasSemi ? ["AppFont", "semibold"] : ["helvetica", "normal"];
  const useBold = hasBold ? ["AppFont", "bold"] : useSemi;
  const useExtraBold = hasXBold ? ["AppFont", "extrabold"] : useBold;

  const setText = (hex) => pdf.setTextColor(hex || "#121B3F");
  function textLine(
    str,
    x,
    y,
    { font = useSemi, size = 10, align = "left" } = {}
  ) {
    pdf.setFont(font[0], font[1]);
    pdf.setFontSize(size);
    pdf.text(String(str ?? ""), x, y, { align });
  }
  function labelValue(label, value, x, y, opts = {}) {
    textLine(label, x, y, { font: useSemi, size: 11, ...opts });
    if (value) textLine(value, x + 28, y, { font: useSemi, size: 10, ...opts });
  }
  async function addImageCover(dataUrl, x, y, w, h) {
    const img = await loadImage(dataUrl, null);
    const iw = img.naturalWidth,
      ih = img.naturalHeight;
    const scale = Math.max(w / iw, h / ih);
    const dw = iw * scale,
      dh = ih * scale;
    const dx = x + (w - dw) / 2;
    const dy = y + (h - dh) / 2;
    pdf.addImage(dataUrl, "PNG", dx, dy, dw, dh);
  }

  async function addImageContain(dataUrl, x, y, w, h) {
    const img = await loadImage(dataUrl, null);
    const iw = img.naturalWidth,
      ih = img.naturalHeight;
    const scale = Math.min(w / iw, h / ih); // contain
    const dw = iw * scale,
      dh = ih * scale;
    const dx = x + (w - dw) / 2;
    const dy = y + (h - dh) / 2;
    pdf.addImage(dataUrl, "PNG", dx, dy, dw, dh);
  }

  // ---------- Header ----------
  // Logo left
  const logoW = 40; // mm
  if (logoSrc) {
    try {
      pdf.addImage(logoSrc, "PNG", m.left, m.top - 7, logoW, 0);
    } catch {
      try {
        pdf.addImage(logoSrc, "SVG", m.left, m.top, logoW, 0);
      } catch {}
    }
  }
  // Title right (Open Sans 600, H5, ~130% line-height feel)
  setText(brand.primary);
  textLine(title, pageW - m.right, m.top + 6, {
    font: useSemi,
    size: 22,
    align: "right",
  });

  // ---------- Columns ----------
  const colW = (pageW - m.left - m.right) / 2; // ≈ 90
  const leftX = m.left;
  const rightX = leftX + colW;
  let y = 48;

  // Order block (left)
  setText("#121B3F");
  labelValue("Order: " + orderNumber, "", leftX, y, {
    size: 12,
    font: useExtraBold,
  });
  y += 7;
  setText(brand.muted);
  labelValue("Driver: " + driverName, "", leftX, y, {
    font: useSemi,
    size: 12,
  });
  y += 7;
  labelValue("Scheduled date: " + (orderDate ? orderDate : ""), "", leftX, y, {
    font: useSemi,
    size: 12,
  });
  y += 12;

  // Customer block (left)
  setText("#121B3F");
  labelValue("Customer: " + customerName, "", leftX, y, {
    font: useBold,
    size: 12,
  });
  y += 7;
  setText(brand.muted);
  labelValue("Pickup address: " + pickupAddress, "", leftX, y, {
    font: useSemi,
    size: 12,
  });
  y += 7;
  labelValue("Dropoff address: " + dropoffAddress, "", leftX, y, {
    font: useSemi,
    size: 12,
  });
  y += 12;

  // ---------- Map (left) ----------
  const mapRect = { x: leftX + 2, y: 104, w: 80, h: 80 }; // mm — bigger box

  // Request enough pixels for crisp print (≈ 8 px per mm)
  const PX_PER_MM = 2; // ~200 dpi equivalent in PDF
  const MAP_PIX_W = Math.round(mapRect.w * PX_PER_MM); // e.g. 50mm → 400px
  const MAP_PIX_H = Math.round(mapRect.h * PX_PER_MM); // e.g. 80mm → 640px

  const mapDataUrl = await renderMapWithTinyMarker({
    mapRef,
    mapboxToken,
    markerLngLat,
    mapWidthPx: MAP_PIX_W,
    mapHeightPx: MAP_PIX_H,
    zoomAdjust: 0.6,
  });

  // Fill the box (crop if aspect differs)
  await addImageCover(mapDataUrl, mapRect.x, mapRect.y, mapRect.w, mapRect.h);

  // ---------- Statuses (right) ----------
  const statusesRect = { x: rightX, y: mapRect.y, w: mapRect.w, h: mapRect.h }; // same size as map

  // Title (centered above the box)
  pdf.setTextColor(brand.primary);
  pdf.setFont(useBold[0], useBold[1]);
  pdf.setFontSize(18);
  pdf.text(
    "Statuses",
    statusesRect.x + 2,
    statusesRect.y + 5 // a little above the box
    // { align: "center" }
  );

  // Content box with small padding inside the statuses box
  const pad = 4; // mm
  const contentRect = {
    x: statusesRect.x + pad + 60,
    y: statusesRect.y + pad + 10,
    w: statusesRect.w - pad * 2 - 5,
    h: statusesRect.h - pad * 2,
  };

  // Capture the infoEl at a FIXED CSS width that matches the PDF box width
  if (infoEl) {
    const cssWidthPx = Math.max(200, Math.round(contentRect.w * 8)); // floor to something sensible
    const { dataURL: infoURL } = await captureNodeAtFixedWidth(
      infoEl,
      cssWidthPx,
      { hideElement }
    );

    // Fill the contentRect (use cover so it always looks full; switch to addImageContain if you prefer no crop)
    await addImageCover(
      infoURL,
      contentRect.x,
      contentRect.y,
      contentRect.w,
      contentRect.h
    );
  }

  // ---------- Bottom row: photo (left) + signature (right) ----------
  const gapW = pageW * 0.03;

  // Lay this row safely below the map/info area, and also ensure ≥5% top margin.
  const rowTop = Math.max(mapRect.y + mapRect.h + 10, pageH * 0.05) + 20;

  // Compute equal sizes for both boxes inside page margins
  const innerLeft = m.left;
  const innerRight = pageW - m.right;
  const contentW = innerRight - innerLeft;
  const boxW = (contentW - gapW) / 2;
  const boxH = 60; // tweak if you want taller/shorter

  // Headings ~6mm above boxes
  pdf.setTextColor(brand.primary);
  pdf.setFont(useBold[0], useBold[1]);
  pdf.setFontSize(18);
  pdf.text("Delivered package photo", innerLeft, rowTop - 6);
  pdf.text("Customer's signature", innerLeft + boxW + gapW, rowTop - 6);

  // Box rects (SAME width/height)
  const photoRect = { x: innerLeft, y: rowTop, w: boxW, h: boxH };
  const signRect = { x: innerLeft + boxW + gapW, y: rowTop, w: boxW, h: boxH };

  // Draw box backgrounds
  pdf.setDrawColor(230);
  pdf.setFillColor(246); // subtle gray for photo box
  pdf.roundedRect(
    photoRect.x,
    photoRect.y,
    photoRect.w,
    photoRect.h,
    3,
    3,
    "FD"
  );

  pdf.setFillColor(255); // white for signature box
  pdf.roundedRect(signRect.x, signRect.y, signRect.w, signRect.h, 3, 3, "FD");

  // --- Fetch assets (unchanged, kept here so imageFile/signatureSvgString are defined) ---
  const authHeader = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  let imageFile = null;
  let signatureSvgString = null;

  try {
    const imgRes = await fetch(`${apiUrl}/get_package_image/${packageId}`, {
      headers: { ...authHeader },
    });
    if (imgRes.ok) {
      const blob = await imgRes.blob();
      const cd = imgRes.headers.get("content-disposition") || "";
      const mfn = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const fallbackName = `package_${packageId}.${
        blob.type.split("/")[1] || "png"
      }`;
      const filename = decodeURIComponent(mfn?.[1] || fallbackName);
      imageFile = new File([blob], filename, { type: blob.type });
    }
  } catch (e) {
    console.warn("Package image fetch failed:", e);
  }

  try {
    const sigRes = await fetch(`${apiUrl}/get_package_signature/${packageId}`, {
      headers: { "Content-Type": "application/json", ...authHeader },
    });
    if (sigRes.ok) {
      const { signature } = await sigRes.json();
      signatureSvgString = signature.p_signature;
    }
  } catch (e) {
    console.warn("Signature fetch failed:", e);
  }

  // --- Place package photo (contain-fit into the photo box) ---
  if (imageFile) {
    const reader = new FileReader();
    const imgDataURL = await new Promise((res, rej) => {
      reader.onload = () => res(reader.result);
      reader.onerror = rej;
      reader.readAsDataURL(imageFile);
    });
    await addImageContain(
      imgDataURL,
      photoRect.x,
      photoRect.y,
      photoRect.w,
      photoRect.h
    );
  } else {
    // Optional placeholder text if missing
    pdf.setTextColor(103, 108, 126);
    pdf.setFont(useSemi[0], useSemi[1]);
    pdf.setFontSize(9);
    pdf.text("Delivered package photo", photoRect.x + 4, photoRect.y + 6);
  }

  // --- Place signature (convert SVG → PNG, then contain-fit in the signature box) ---
  if (signatureSvgString && signatureSvgString.trim()) {
    try {
      const sigPngUrl = await svgToPngDataUrlFromSrc(signatureSvgString, 2);
      await addImageContain(
        sigPngUrl,
        signRect.x,
        signRect.y,
        signRect.w,
        signRect.h
      );
    } catch (e) {
      console.warn("Signature render failed, showing placeholder:", e);
      pdf.setTextColor(103, 108, 126);
      pdf.setFont(useSemi[0], useSemi[1]);
      pdf.setFontSize(9);
      pdf.text("Customer's signature", signRect.x + 4, signRect.y + 6);
    }
  } else {
    pdf.setTextColor(103, 108, 126);
    pdf.setFont(useSemi[0], useSemi[1]);
    pdf.setFontSize(9);
    pdf.text("Customer's signature", signRect.x + 4, signRect.y + 6);
  }

  // Footer
  setText(brand.primary);
  textLine("Thank you for your order", pageW / 2, pageH - m.bottom + 4, {
    font: useBold,
    size: 18,
    align: "center",
  });

  pdf.save(fileName);
}

async function svgToPngDataUrlFromSrc(srcOrText, scale = 2) {
  // 1) Get the SVG text
  let svgText;
  if (typeof srcOrText === "string" && srcOrText.trim().startsWith("<svg")) {
    svgText = srcOrText;
  } else if (typeof srcOrText === "string") {
    // URL or data URL
    const res = await fetch(srcOrText);
    svgText = await res.text();
  } else {
    throw new Error("signature source not a string");
  }

  // 2) Ensure basic attributes exist
  // add xmlns if missing (common bug)
  if (!/xmlns=/.test(svgText)) {
    svgText = svgText.replace(
      /<svg\b/,
      '<svg xmlns="http://www.w3.org/2000/svg"'
    );
  }

  const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
  const svg = doc.documentElement;

  // Compute dimensions from width/height or viewBox
  const vbAttr = svg.getAttribute("viewBox");
  let vb = vbAttr ? vbAttr.split(/[,\s]+/).map(Number) : null;
  let w = parseFloat(svg.getAttribute("width") || "");
  let h = parseFloat(svg.getAttribute("height") || "");
  if (!(w > 0 && h > 0)) {
    if (vb && vb.length === 4 && vb[2] > 0 && vb[3] > 0) {
      w = vb[2];
      h = vb[3];
    } else {
      // fallback if the SVG has no sizing at all
      w = 600;
      h = 200;
    }
  }

  // 3) Blob → Image → Canvas
  const blob = new Blob([svg.outerHTML], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url, null); // IMPORTANT: no crossOrigin for blob/data
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext("2d");
    if (scale !== 1) ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}
